/**
 * The list of jobs or tasks.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import RectRoundButton from '@/components/Button/RectRoundButton'
import { SortTableHeader } from '@/components/jobui/SortTableHeader'
import {
  getModuleByServiceName,
  getModuleDisplayName,
} from '@/components/ModuleConfig/ModuleConfigList'
import '@/pages/TaskCreation/ModuleSelector.css'
import { JobDetail } from '@amplify3/duplo-back-end'
import { useEffect, useRef, useState } from 'react'
import {
  BiBot,
  BiCoinStack,
  BiHive,
  BiLoaderCircle,
  BiSolidWatch,
  BiTagAlt,
} from 'react-icons/bi'
import './TaskList.css'
import TaskListRow from './TaskListRow'

interface TaskListProps {
  taskList: JobDetail[]
  lastTaskId?: string
  requestMoreTasks: () => Promise<void>
}

const TaskList = (props: TaskListProps) => {
  const tableBodyRef = useRef(null)

  const [filters, setFilters] = useState<string[]>([])
  const [isLoadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    // Task list has changed: set the state `isLoading` to false
    // meaning change is completed.
    setLoadingMore(false)
  }, [props.taskList])

  const modules = [
    ...new Set(
      props.taskList.flatMap((elt) => getModuleByServiceName(elt?.service_name))
    ),
  ].filter((elt) => elt !== undefined)

  const filteredJobs = props.taskList.filter((elt) =>
    filters.length > 0
      ? filters.includes(getModuleDisplayName(elt?.service_name))
      : true
  )

  const columnHeaderList = [
    { name: 'Name', icon: <BiTagAlt /> },
    { name: 'Module', icon: <BiHive /> },
    { name: 'Status', icon: <BiLoaderCircle /> },
    { name: 'Credits', icon: <BiCoinStack /> },
    { name: 'Output', icon: <BiBot /> },
  ]

  const onFilterClick = (module: string) => {
    const copy = [...filters]
    const copyIndex = copy.findIndex((elt) => elt === module)
    if (copyIndex > -1) {
      copy.splice(copyIndex, 1)
    } else {
      copy.push(module)
    }
    setFilters(copy)
  }

  /**
   * Handle the click on the button "Load More".
   *
   * The state `isLoading` is set to true. It will be set to false
   * in a `useEffect` when the prop `taskList` will change.
   */
  const onLoadMoreClick = async () => {
    if (!isLoadingMore) {
      setLoadingMore(true)
      await props.requestMoreTasks()
    }
  }

  return (
    <div className="task-list">
      {modules.length > 0 && (
        <div className="filter-container">
          <div className="filter-container-header">
            <div className="filter-button-container">
              <div className="filter-title">Filters</div>
              <RectRoundButton
                id="filter-reset"
                onClick={() => setFilters([])}
                label="Reset"
              />
            </div>
          </div>

          <div className="filter-item-container">
            {modules.map((module, index) => (
              <button
                key={`${module.info.displayModuleName}_${index}`}
                className={
                  filters.includes(module.info.displayModuleName)
                    ? 'moduleTag'
                    : 'moduleTag notSelected'
                }
                type="button"
                onClick={() => {
                  onFilterClick(module.info.displayModuleName)
                }}
              >
                {module.info.displayModuleName}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="jobui-table">
        <div className="jobui-table-inner">
          <table>
            <thead>
              <tr>
                <SortTableHeader name={'Created'} icon={<BiSolidWatch />} />
                {columnHeaderList.map(({ name, icon }) => (
                  <th key={name} className="resultCol">
                    <div className="SortTableHeader">
                      {icon}
                      {name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            {filteredJobs !== undefined && filteredJobs.length > 0 && (
              <tbody ref={tableBodyRef}>
                {filteredJobs.map((job) => (
                  <TaskListRow key={job?.job_id} job={job} />
                ))}
              </tbody>
            )}
          </table>
        </div>

        {props.taskList.length !== undefined &&
          props.taskList.length > 0 &&
          props.lastTaskId !== undefined && (
            <button
              type="button"
              id="LoadMoreButton"
              className="button-dark"
              disabled={isLoadingMore}
              onClick={onLoadMoreClick}
            >
              <div className="button-label">
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </div>
            </button>
          )}
      </div>
    </div>
  )
}

export default TaskList
